import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Building2, Hammer, ArrowRight, Sparkles, Shield } from "lucide-react";
import { useConfig } from "../lib/ConfigContext";
import { AuthFooterNote, AuthHeader } from "../components/AuthUI";
import usePageTitle from "../lib/usePageTitle";

/**
 * v2.60 — Access page: 4 role cards (Client / Dealer / Builder-Owner / Team) +
 * top "Already have an account?" sign-in link. Everything admin-editable via
 * /admin?tab=content → "Access Page · Onboarding".
 */
const ROLE_META = {
  client: {
    icon: Home,
    href_register: "/register?role=client",
    href_login: "/login?role=client",
    accent: "bg-[#EEF4FF] text-[#0044CC]",
    cardAccent: "from-[#EEF4FF] via-white to-white",
  },
  dealer: {
    icon: Building2,
    href_register: "/register?role=dealer",
    href_login: "/login?role=dealer",
    accent: "bg-gradient-to-br from-[#0055FF] to-[#2D7BFF] text-white",
    cardAccent: "from-[#EEF4FF] via-white to-white",
    glow: true,
  },
  builder: {
    icon: Hammer,
    href_register: "/register?role=builder",
    href_login: "/login?role=builder",
    accent: "bg-[#F5F3FF] text-[#6D28D9]",
    cardAccent: "from-[#F5F3FF] via-white to-white",
  },
  team: {
    icon: Shield,
    href_register: "/team-login",
    href_login: "/team-login",
    accent: "bg-[#F0FDF4] text-[#15803D]",
    cardAccent: "from-[#F0FDF4] via-white to-white",
    secure: true,
    teamOnly: true,
  },
};

export default function AccessHouseEntry() {
  usePageTitle("Access Your Workspace | HouseEntry");
  const { content } = useConfig();
  const ap = content?.access_page || {};
  const cards = ["client", "dealer", "builder", "team"]
    .map((k) => ({ key: k, ...(ap[k] || {}) }))
    .filter((c) => c.enabled !== false);

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-50" data-testid="access-page">
      <div className="auth-blobs opacity-30" />

      <div className="relative max-w-[1180px] mx-auto px-4 sm:px-8 lg:px-10 pt-4 pb-10 sm:pt-6 sm:pb-14">
        <AuthHeader showBack backTo="/" />

        {/* Top "Already have account?" sign-in link */}
        {ap.show_top_signin !== false && (
          <div className="flex justify-center mt-2 mb-5" data-testid="access-top-signin">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-zinc-200 hover:border-[#0055FF] hover:bg-[#EEF4FF] transition-colors text-[12.5px] font-semibold text-zinc-700 hover:text-[#0044CC] shadow-sm"
              data-testid="link-existing-login"
            >
              <span className="text-zinc-500">Already have an account?</span>
              <span className="text-[#0055FF]">Sign in →</span>
            </Link>
          </div>
        )}

        {/* Hero — compact on mobile, generous on desktop */}
        <div className="max-w-2xl mx-auto text-center mt-1 mb-6 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full bg-[#EEF4FF] border border-[#C7D9FF] text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#0044CC]" data-testid="access-eyebrow">
            <Sparkles size={11} /> {ap.eyebrow || "Premium Proptech Platform"}
          </div>
          <h1 className="font-heading font-bold text-[26px] sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-zinc-900" data-testid="access-heading">
            {ap.heading || "Access Your Real Estate Workspace"}
          </h1>
          <p className="mt-3 sm:mt-4 text-[14px] sm:text-base text-zinc-600 leading-relaxed" data-testid="access-sub">
            {ap.sub || "Verified listings. Smart leads. Faster deals."}
          </p>
        </div>

        {/* Role cards — stack on mobile, 2-col tablet, 4-col desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-testid="role-grid">
          {cards.map((c) => (
            <RoleCard key={c.key} roleKey={c.key} card={c} />
          ))}
        </div>

        <AuthFooterNote className="mt-10" />
      </div>
    </div>
  );
}

function RoleCard({ roleKey, card }) {
  const nav = useNavigate();
  const meta = ROLE_META[roleKey] || ROLE_META.client;
  const Icon = meta.icon;
  const showSecondary = !!(card.secondary_label && card.secondary_label.trim());
  return (
    <div
      className={`relative h-full rounded-2xl ${meta.glow ? "glow-border" : ""}`}
      data-testid={`role-card-${roleKey}`}
    >
      <div className={`relative h-full flex flex-col bg-gradient-to-br ${meta.cardAccent} border border-zinc-200 rounded-2xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 hover:border-[#0055FF]/30`}>
        {meta.secure && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-full bg-green-100 text-green-800 border border-green-200">
            <Shield size={9} /> Secure
          </span>
        )}
        <div className="flex items-start gap-3 mb-3 sm:mb-4">
          <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl ${meta.accent} flex items-center justify-center shadow-sm shrink-0`}>
            <Icon size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-[17px] sm:text-xl tracking-tight text-zinc-900 leading-tight" data-testid={`role-card-${roleKey}-title`}>
              {card.title || roleKey}
            </h3>
          </div>
        </div>
        <p className="text-[13px] sm:text-[13.5px] text-zinc-600 leading-relaxed mb-5 flex-1" data-testid={`role-card-${roleKey}-subtext`}>
          {card.subtext || ""}
        </p>

        {/* Primary (Register) — blue, full-width, prominent */}
        <button
          onClick={() => nav(meta.href_register)}
          className="group w-full h-12 rounded-xl btn-gradient text-[14px] font-bold inline-flex items-center justify-center gap-2 shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-[0.99] transition-all"
          data-testid={`role-card-${roleKey}-primary`}
        >
          {card.primary_label || "Continue"}
          <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
        </button>

        {/* Secondary (Login) — smaller, ghost link. Hidden when label is empty */}
        {showSecondary && (
          <Link
            to={meta.href_login}
            className="mt-2 w-full h-10 rounded-xl text-[12.5px] font-semibold text-zinc-600 inline-flex items-center justify-center hover:text-[#0055FF] hover:bg-[#EEF4FF] transition-colors"
            data-testid={`role-card-${roleKey}-secondary`}
          >
            {card.secondary_label}
          </Link>
        )}
      </div>
    </div>
  );
}
